/**
 * Upsell Popup — global singleton
 *
 * Works on every page (product pages, collection pages, etc.).
 * The popup DOM is created by this script and appended to <body>.
 * Upsell product data is read from data-upsell-* attributes on the
 * <product-form> element, which are output by Liquid for any product
 * that has the `custom.upsell_product` metafield set.
 *
 * Intercepts ALL <product-form> submit buttons via a capture-phase
 * click listener. A MutationObserver re-attaches when quick-add
 * modals inject new product-form elements into the page.
 */

(function () {
  'use strict';

  let _popup = null;   // the overlay element (created once, stays in DOM)
  let _confirmed = false;
  let _loading = false;
  let _activeButton = null;
  let _upsellVariantId = null;
  let _lastPrice = '';
  let _lastComparePrice = '';

  // ── Upsell pair tracking (sessionStorage) ────────────────────────────

  const PAIRS_KEY = 'upsell_pairs';

  function _loadPairs() {
    try { return JSON.parse(sessionStorage.getItem(PAIRS_KEY) || '{}'); }
    catch (e) { return {}; }
  }

  function _savePair(mainVariantId, upsellVariantId) {
    var pairs = _loadPairs();
    pairs[String(mainVariantId)] = String(upsellVariantId);
    sessionStorage.setItem(PAIRS_KEY, JSON.stringify(pairs));
  }

  function _removePair(mainVariantId) {
    var pairs = _loadPairs();
    delete pairs[String(mainVariantId)];
    sessionStorage.setItem(PAIRS_KEY, JSON.stringify(pairs));
  }

  // ── Remove upsell from cart and refresh UI ────────────────────────────

  function _removeFromCart(variantId) {
    console.log('[upsell] _removeFromCart called for variant:', variantId);
    var updates = {};
    updates[variantId] = 0;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      // Request cart-icon-bubble section HTML alongside the cart update so we
      // can refresh the count bubble without a page reload.
      body: JSON.stringify({
        updates: updates,
        sections: 'cart-icon-bubble',
        sections_url: window.location.pathname,
      }),
    })
      .then(function (r) { return r.json(); })
      .then(function (cartData) {
        // Directly swap the cart-icon-bubble section HTML using the rendered
        // fragment Shopify returns — same mechanism Dawn's own cart JS uses:
        // getElementById('cart-icon-bubble') is the <a> tag; its innerHTML is
        // replaced with the inner content of .shopify-section from the section.
        if (cartData.sections && cartData.sections['cart-icon-bubble']) {
          var bubbleEl = document.getElementById('cart-icon-bubble');
          if (bubbleEl) {
            var parsed = new DOMParser().parseFromString(
              cartData.sections['cart-icon-bubble'],
              'text/html'
            );
            var newContent = parsed.querySelector('.shopify-section');
            if (newContent) bubbleEl.innerHTML = newContent.innerHTML;
          }
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'upsell-popup', cartData: cartData });
        }
      })
      .catch(function (e) { console.error('[upsell-popup] failed to remove upsell:', e); });
  }

  // ── Watch for main product removal ───────────────────────────────────

  /**
   * Primary method: intercept cart-remove-button clicks directly.
   * The inner <button> carries data-variant-id so we know exactly which
   * product is being removed without having to parse the cart response.
   * We save the upsell variant ID we need to remove, then act after the
   * cart update completes (so the UI re-renders in the right order).
   */
  function _initRemoveButtonListener() {
    document.addEventListener('click', function (e) {
      var removeEl = e.target.closest('cart-remove-button');
      if (!removeEl) return;

      // The rendered button inside cart-remove-button carries the variant id
      var variantBtn = removeEl.querySelector('[data-variant-id]');
      if (!variantBtn) return;

      var removedVariantId = String(variantBtn.dataset.variantId);
      var pairs = _loadPairs();
      console.log('[upsell] Remove button clicked. variantId:', removedVariantId, 'stored pairs:', JSON.stringify(pairs));
      var upsellVariantId = pairs[removedVariantId];
      if (!upsellVariantId) {
        console.log('[upsell] No upsell pair found for variant', removedVariantId);
        return;
      }
      console.log('[upsell] Will remove upsell variant:', upsellVariantId);

      // Delete the pair immediately so the cartUpdate watcher doesn't also fire
      _removePair(removedVariantId);

      // Wait for the cart update to finish, then remove the upsell
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
        // pubsub not available — remove with a small delay as fallback
        setTimeout(function () { _removeFromCart(upsellVariantId); }, 800);
        return;
      }

      var unsubscribeFn;
      unsubscribeFn = subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        if (event.source === 'upsell-popup') return;
        unsubscribeFn();
        _removeFromCart(upsellVariantId);
      });
    }, true); // capture phase — fires before CartRemoveButton's own handler
  }

  /**
   * Fallback method: watch cartUpdate events for quantity-to-zero changes
   * (quantity input) or any other cart change we didn't intercept above.
   */
  function _initCartWatcher() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (event.source === 'upsell-popup') return;
      if (!event.cartData || !Array.isArray(event.cartData.items)) return;

      var pairs = _loadPairs();
      if (!Object.keys(pairs).length) return;

      var variantIdsInCart = event.cartData.items.map(function (item) {
        return String(item.variant_id);
      });

      Object.keys(pairs).forEach(function (mainVariantId) {
        if (variantIdsInCart.indexOf(mainVariantId) !== -1) return; // still in cart

        var upsellVariantId = pairs[mainVariantId];
        _removePair(mainVariantId);

        if (variantIdsInCart.indexOf(upsellVariantId) !== -1) {
          _removeFromCart(upsellVariantId);
        }
      });
    });
  }

  // ── Popup DOM (created once) ──────────────────────────────────────────

  function _getOrCreatePopup() {
    if (_popup) return _popup;

    const wrapper = document.createElement('div');
    var config = window.upsellPopupConfig || {};
    var logoHtml = config.logoUrl
      ? '<div class="upsell-popup__logo-wrapper"><img class="upsell-popup__logo" src="' + config.logoUrl + '" alt="' + (config.logoAlt || '') + '" width="200" loading="lazy"></div>'
      : '';

    wrapper.innerHTML = `
      <div id="upsell-popup-overlay" class="upsell-popup__overlay" aria-hidden="true">
        <div class="upsell-popup__modal" role="dialog" aria-modal="true"
             aria-labelledby="upsell-popup-title" tabindex="-1">
          <button type="button" class="upsell-popup__close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18"
                 aria-hidden="true" focusable="false" fill="none"
                 stroke="currentColor" stroke-width="2" class="icon icon-close">
              <path d="M1 17L17 1M1 1l16 16"/>
            </svg>
          </button>
          ${logoHtml}
          <p class="upsell-popup__eyebrow">Wait! Customers also love&hellip;</p>
          <div class="upsell-popup__body">
            <div class="upsell-popup__image-wrapper">
              <img class="upsell-popup__image" src="" alt=""
                   width="130" height="130" loading="lazy">
            </div>
            <div class="upsell-popup__product-info">
              <h2 id="upsell-popup-title" class="upsell-popup__product-title"></h2>
              <p class="upsell-popup__desc"></p>
            </div>
          </div>
          <div class="upsell-popup__actions">
            <button type="button"
                    class="upsell-popup__add-both button button--full-width button--primary">
              YES! ADD 2 MORE FOR <span class="upsell-popup__add-both-price"></span>
            </button>
            <button type="button"
                    class="upsell-popup__decline button button--full-width button--secondary">
              No Thanks
            </button>
          </div>
        </div>
      </div>`;

    _popup = wrapper.firstElementChild;
    document.body.appendChild(_popup);

    // Backdrop click closes popup and adds original product to cart
    _popup.addEventListener('click', function (e) {
      if (e.target === _popup) _dismiss();
    });

    // Escape key closes popup and adds original product to cart
    document.addEventListener('keyup', function (e) {
      if (e.key === 'Escape' && _popup.classList.contains('is-open')) _dismiss();
    });

    _popup.querySelector('.upsell-popup__close').addEventListener('click', _dismiss);

    // "No thanks" — proceed with only the main product
    _popup.querySelector('.upsell-popup__decline').addEventListener('click', _dismiss);

    // "Add both" — add upsell via AJAX then submit the main product form
    _popup.querySelector('.upsell-popup__add-both').addEventListener('click', function () {
      if (_loading) return;
      _setLoading(true);

      // Capture main variant ID so we can auto-remove the upsell if main is removed
      var mainVariantId = null;
      if (_activeButton) {
        var pf = _activeButton.closest('product-form');
        var mainInput = pf ? pf.querySelector('input[name="id"]') : null;
        mainVariantId = mainInput ? mainInput.value : null;
      }
      console.log('[upsell] Add Both clicked. mainVariantId:', mainVariantId, 'upsellVariantId:', _upsellVariantId);

      _addToCart(_upsellVariantId)
        .then(function () {
          if (mainVariantId && _upsellVariantId) {
            _savePair(mainVariantId, _upsellVariantId);
            console.log('[upsell] Pair saved:', mainVariantId, '->', _upsellVariantId);
          } else {
            console.warn('[upsell] Pair NOT saved — mainVariantId:', mainVariantId, 'upsellVariantId:', _upsellVariantId);
          }
          var btn = _activeButton;
          _close();
          _triggerConfirmed(btn);
        })
        .catch(function () {
          _setLoading(false);
        });
    });

    return _popup;
  }

  // ── Dismiss (close + add original product to cart) ───────────────────

  // Used by X button, backdrop click, Escape, and "No Thanks".
  // Captures _activeButton before _close() nulls it, then triggers the
  // original form submission so the product is added and the cart slides out.
  function _dismiss() {
    var btn = _activeButton;
    _close();
    _triggerConfirmed(btn);
  }

  // ── Open / close ─────────────────────────────────────────────────────

  function _open(data) {
    var popup = _getOrCreatePopup();

    // Image
    var imgWrapper = popup.querySelector('.upsell-popup__image-wrapper');
    var img = popup.querySelector('.upsell-popup__image');
    if (data.image) {
      img.src = data.image;
      img.alt = data.imageAlt || '';
      imgWrapper.hidden = false;
    } else {
      imgWrapper.hidden = true;
    }

    // Title
    popup.querySelector('.upsell-popup__product-title').textContent = data.title || '';

    // Desc (under product title)
    var descEl = popup.querySelector('.upsell-popup__desc');
    descEl.textContent = data.desc || '';
    descEl.hidden = !data.desc;

    // Eyebrow
    popup.querySelector('.upsell-popup__eyebrow').textContent = data.eyebrow || 'Wait! Customers also love\u2026';

    // Decline button label
    popup.querySelector('.upsell-popup__decline').textContent = 'No Thanks';

    // Reset add-both button state — rebuild innerHTML so the price span always
    // exists even if a previous _setLoading(true) wiped it via textContent.
    _lastPrice = data.price || '';
    _lastComparePrice = data.comparePrice || '';
    var addBothBtn = popup.querySelector('.upsell-popup__add-both');
    addBothBtn.removeAttribute('aria-disabled');
    addBothBtn.innerHTML = 'YES! ADD 2 MORE FOR&nbsp;' +
      (_lastComparePrice ? '<s class="upsell-popup__add-both-compare-price">' + _lastComparePrice + '</s>' : '') +
      '<span class="upsell-popup__add-both-price" data-price="' + _lastPrice + '" data-compare-price="' + _lastComparePrice + '">' + _lastPrice + '</span>';

    // Show
    popup.classList.add('is-open');
    popup.removeAttribute('aria-hidden');
    document.body.classList.add('upsell-popup-open');
    requestAnimationFrame(function () {
      popup.querySelector('.upsell-popup__modal').focus();
    });
  }

  function _close() {
    if (!_popup) return;
    _popup.classList.remove('is-open');
    _popup.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('upsell-popup-open');
    _confirmed = false;
    _loading = false;
    _activeButton = null;
    _upsellVariantId = null;
  }

  // ── Cart interaction ──────────────────────────────────────────────────

  function _triggerConfirmed(btn) {
    if (!btn) return;
    _confirmed = true;
    btn.click();
    _confirmed = false;
  }

  function _addToCart(variantId) {
    var fd = new FormData();
    fd.append('id', variantId);
    fd.append('quantity', '1');
    return fetch('/cart/add.js', {
      method: 'POST',
      body: fd,
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(function (r) {
      if (!r.ok) throw new Error('cart/add failed');
      return r.json();
    });
  }

  function _setLoading(isLoading) {
    _loading = isLoading;
    if (!_popup) return;
    var btn = _popup.querySelector('.upsell-popup__add-both');
    if (isLoading) {
      btn.setAttribute('aria-disabled', 'true');
      btn.textContent = 'Adding\u2026';
    } else {
      btn.removeAttribute('aria-disabled');
      
btn.innerHTML = 'YES! ADD 2 MORE FOR ' +
  '<span class="upsell-popup__add-both-price" data-price="' + _lastPrice + '" data-compare-price="' + _lastComparePrice + '">' + _lastPrice + '</span>';
    }
  }

  // ── Button interception ───────────────────────────────────────────────

  function _interceptButton(btn) {
    if (btn.__upsellIntercepted) return;
    btn.__upsellIntercepted = true;

    btn.addEventListener(
      'click',
      function (e) {
        // Already confirmed by popup action — let the click through
        if (_confirmed) { e.stopImmediatePropagation(); return; }

        // Disabled buttons should never trigger the popup
        if (btn.hasAttribute('disabled') || btn.getAttribute('aria-disabled') === 'true') return;

        // Walk up to find the product-form with upsell data
        var pf = btn.closest('product-form');
        if (!pf || !pf.dataset.upsellVariantId) return;

        // We have an upsell — intercept this click
        e.preventDefault();
        e.stopImmediatePropagation();

        _activeButton = btn;
        _upsellVariantId = pf.dataset.upsellVariantId;

        _open({
          title: pf.dataset.upsellTitle || '',
          price: pf.dataset.upsellPrice || '',
          comparePrice: pf.dataset.upsellComparePrice || '',
          desc: pf.dataset.upsellDesc || '',
          image: pf.dataset.upsellImage || '',
          imageAlt: pf.dataset.upsellImageAlt || '',
          mainProductTitle: pf.dataset.mainProductTitle || '',
          eyebrow: pf.dataset.upsellEyebrow || '',
        });
      },
      true // capture phase — fires before product-form's own submit handler
    );
  }

  function _attachToAllProductForms() {
    document.querySelectorAll('product-form').forEach(function (pf) {
      var btn = pf.querySelector('[type="submit"]');
      if (btn) _interceptButton(btn);
    });
  }

  // ── Initialise ────────────────────────────────────────────────────────

  // _initRemoveButtonListener uses event delegation on document, so it can
  // run immediately — no need to wait for DOMContentLoaded.
  _initRemoveButtonListener();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      _attachToAllProductForms();
      _initCartWatcher();
    });
  } else {
    _attachToAllProductForms();
    _initCartWatcher();
  }

  // Re-attach when quick-add modals inject new product-form elements
  new MutationObserver(_attachToAllProductForms).observe(document.body, {
    childList: true,
    subtree: true,
  });
})();
